import React, { useState } from 'react';
import { I18n } from 'react-redux-i18n';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import SettingsIcon from '@material-ui/icons/Settings';
import InfoIcon from '@material-ui/icons/Info';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import Tooltip from '@material-ui/core/Tooltip';
import PartnerContact from '../PartnerContact';
import PartnerInfoDialog from '../PartnerInfoDialog';
import CardMenu from '../CardMenu';
import DeletePartnerDialog from '../DeletePartnerDialog';
import { PARTNERS } from '../../../Containers/Partners/config';
import { getPartnerActionMenu } from '../../../Containers/Partners/utils';
import { postStepStatus } from '../../../Containers/Partners/service/partnersApi/actions';
import { deletePartnerConfig } from '../../../Containers/Partners/service/partnersConfig/actions';
import './style.scss';

/**
 * Accès configuration (roue crantée) d'un partenaire configuré :
 *   - composant interne (PARTNERS[prefix].component) -> lien interne /partners/{prefix}
 *   - sinon onboarding.link                          -> lien externe
 *   - sinon                                          -> pas de roue crantée
 */
const getConfigLink = (prefix, onboarding) => {
	if (prefix && PARTNERS[prefix] && PARTNERS[prefix].component) {
		return { type: 'internal', to: `/partners/${prefix}` };
	}
	if (onboarding && onboarding.link) {
		return { type: 'external', href: onboarding.link };
	}
	return null;
};

/**
 * Carte partenaire — état "Configuré" (actif / inactif).
 * Base neuve : composant présentationnel alimenté par /partners/all.
 *
 * @param {Object} props.partner partenaire renvoyé par /partners/all
 */
const CardActifInactif = ({
	partner = {},
	postStepStatus,
	deletePartnerConfig,
	dispatchPartnerMenuAction,
}) => {
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [infoOpen, setInfoOpen] = useState(false);
	const {
		logo,
		name,
		prefix,
		active,
		infos,
		onboarding,
		partner_contact,
		documentation,
	} = partner;

	const title = name || (prefix ? I18n.t(`partner.prefix.${prefix}`) : '');
	const isActive = !!active;

	// Activation / désactivation : pour un partenaire configuré (step "success"),
	// l'API route ce POST vers le step set_active (mask, lien auth, status et
	// effets par partenaire gérés côté backend). Le partenaire renvoyé met à jour
	// le store → le switch reflète le nouvel état.
	const toggleActive = () => postStepStatus({ prefix, data: { active: !isActive } });

	const configLink = getConfigLink(prefix, onboarding);
	const hasInfos = infos && Object.keys(infos).length > 0;

	// Actions propres au partenaire, déclarées dans PARTNERS[prefix].menu
	// (ex. "Synchroniser les réservations" pour zen / kouver) : action redux
	// brute { type, ...data }, écoutée par la saga partnersConfig.
	const partnerMenu = getPartnerActionMenu(prefix) || [];

	// Suppression de la liaison : validation écrite (cf. DeletePartnerDialog).
	const menuItems = [
		...partnerMenu.map((option, index) => ({
			key: option.type || index,
			label: I18n.t(option.label),
			icon: option.icon,
			onClick: () => dispatchPartnerMenuAction(option.type, option.data),
		})),
		{
			key: 'delete',
			label: I18n.t('partner.delete.menu_binding'),
			icon: DeleteOutlineIcon,
			danger: true,
			onClick: () => setDeleteOpen(true),
		},
	];

	const gear = (
		<Tooltip title={I18n.t('partner.tooltip_partner_edit')}>
			<div className="card-active__icon">
				<SettingsIcon />
			</div>
		</Tooltip>
	);

	return (
		<div className={`card-active${isActive ? '' : ' card-active--inactive'}`}>
			<div className="card-active__top">
				{hasInfos ? (
					<Tooltip title={I18n.t('partner.tooltip_partner_info')}>
						<div
							className="card-active__icon"
							onClick={() => setInfoOpen(true)}>
							<InfoIcon />
						</div>
					</Tooltip>
				) : (
					<span />
				)}

				<div className="card-active__top-right">
					<PartnerContact
						name={title}
						contact={partner_contact}
						documentation={documentation}
					/>
					{configLink && configLink.type === 'internal' && (
						<Link to={configLink.to}>{gear}</Link>
					)}
					{configLink && configLink.type === 'external' && (
						<a href={configLink.href}>{gear}</a>
					)}
					<CardMenu items={menuItems} />
				</div>
			</div>

			<div className="card-active__logo">
				{logo ? (
					<img src={logo} alt={`Logo ${title}`} />
				) : (
					<span className="card-active__title">{title}</span>
				)}
			</div>

			<div className="card-active__bottom">
				<label className="card-active__switch">
					<span>
						{isActive ? I18n.t('partner.active') : I18n.t('partner.inactive')}
					</span>
					<input
						type="checkbox"
						checked={isActive}
						onChange={toggleActive}
					/>
					<i />
				</label>
			</div>

			<PartnerInfoDialog
				open={infoOpen}
				partnerName={title}
				infos={infos}
				onClose={() => setInfoOpen(false)}
			/>

			<DeletePartnerDialog
				open={deleteOpen}
				partnerName={title}
				strict
				onClose={() => setDeleteOpen(false)}
				onConfirm={() => deletePartnerConfig({ prefix })}
			/>
		</div>
	);
};

const mapDispatchToProps = dispatch => ({
	postStepStatus: body => dispatch(postStepStatus(body)),
	deletePartnerConfig: body => dispatch(deletePartnerConfig(body)),
	dispatchPartnerMenuAction: (type, data) => dispatch({ type, ...data }),
});

export default connect(null, mapDispatchToProps)(CardActifInactif);
